import { networkStore } from "./network";
import { assertPrivateSession, getPrivateSessionState } from "./private-session";
import type { SellerSession } from "./types";
/**
 * The one place this app talks to the API.
 *
 * `credentials: "include"` plus an ABSOLUTE `VITE_API_BASE_URL` is the whole
 * contract. In production the SPA is served from `restaurant.trioty.com` and
 * the API lives at `api.trioty.com` — a different origin — so:
 *
 * - a relative `fetch("/api/…")` would hit this app's own SPA catch-all and
 *   come back as `index.html` with status **200**, which is why the lint rule
 *   forbids one everywhere except this file; and
 * - the session cookie is scoped to the shared apex, which is exactly why the
 *   app lives on `restaurant.<domain>` rather than a separate brand apex
 *   (decision D1). On another apex that cookie is cross-site and simply never
 *   sent, and every request here would 401.
 */

export class ApiError extends Error {
  readonly status: number;
  /** The backend's typed `code`, when it sent one. */
  readonly code: string | null;
  readonly body: unknown;

  // Fields assigned in the body rather than as constructor parameter
  // properties: this app compiles with `erasableSyntaxOnly`, which forbids the
  // shorthand because it emits runtime code rather than erasing to nothing.
  constructor(
    status: number,
    code: string | null,
    message: string,
    body: unknown,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

function getApiBaseUrl(): string {
  const configured = import.meta.env.VITE_API_BASE_URL;
  if (!configured) {
    throw new Error(
      "VITE_API_BASE_URL is not set. This app cannot use a relative API path — see the note in client.ts.",
    );
  }
  return configured.replace(/\/+$/, "");
}


/**
 * The message out of an error body, whatever shape the server sent it in.
 *
 * Nest's exception filter sends a STRING for a thrown `HttpException` and an
 * ARRAY for a `ValidationPipe` refusal — one entry per failed constraint. This
 * read used to accept the string only, so every out-of-range settings save
 * surfaced as the bare English `statusText` ("Bad Request") on an Arabic
 * screen, with the real reason ("refundLateAfterMinutes must not be less than
 * 5") sitting unread in the array beside it (UI MAJOR 6).
 *
 * The array is joined rather than truncated to its first entry: a form that
 * failed two constraints has two things wrong with it, and naming one of them
 * sends the user round a second time.
 */
function messageFromBody(record: Record<string, unknown>, fallback: string): string {
  if (typeof record.message === "string" && record.message !== "") {
    return record.message;
  }
  if (Array.isArray(record.message)) {
    const parts = record.message.filter(
      (entry): entry is string => typeof entry === "string" && entry !== "",
    );
    if (parts.length > 0) return parts.join(" · ");
  }
  return fallback;
}

/**
 * `body` is the JSON VALUE, never the encoded string — `apiFetch` below does
 * the `JSON.stringify`.
 *
 * It was typed `unknown`, which accepted a pre-stringified body as happily as
 * an object, and four call sites on two back-office screens passed one. The
 * server then received a JSON *string* where it expected an object, refused
 * every write with a 400, and the screen rendered the browser's own
 * `Unexpected token '"' ... is not valid JSON` as the failure message. Both
 * screens — delivery platforms and suppliers — could not create, edit or
 * retire anything at all.
 *
 * Narrowing it to an object or an array is what makes that a COMPILE error
 * rather than a shape only a live request can catch: `string` is no longer
 * assignable, so `body: JSON.stringify(x)` cannot be written again.
 */
type ApiFetchOptions = Omit<RequestInit, "body"> & {
  body?: Record<string, unknown> | readonly unknown[];
  sessionChange?: true;
};

export async function apiFetch<T>(
  path: string,
  options: ApiFetchOptions = {},
): Promise<T> {
  const privateRequest = !(options.sessionChange && path === "/seller/password") && /^\/(?:restaurant|seller)\//.test(path) && !/^\/seller\/auth\/(?:logout|switch-store)(?:\?|$)/.test(path);
  const generation = getPrivateSessionState().generation;
  if (privateRequest) assertPrivateSession(generation);
  const { body, headers, sessionChange: _sessionChange, ...rest } = options;
  const response = await fetch(`${getApiBaseUrl()}${path}`, {
    ...rest,
    credentials: "include",
    headers: {
      ...(body === undefined ? {} : { "Content-Type": "application/json" }),
      ...headers,
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  }).catch(failure => {
    if (privateRequest) assertPrivateSession(generation);
    // A `TypeError` is fetch's "the connection did not happen" (an abort is a
    // DOMException, a 4xx/5xx never throws). Verified by a probe before the
    // offline screen believes it — see `network.ts`.
    if (failure instanceof TypeError) networkStore.reportFailure();
    throw failure;
  });
  networkStore.reportSuccess();

  if (privateRequest) assertPrivateSession(generation);
  if (response.status === 204) {
    return undefined as T;
  }

  const payload = await response.json().catch(() => null);
  if (privateRequest) assertPrivateSession(generation);

  if (!response.ok) {
    // The backend's typed codes are the contract this app branches on — a
    // stale-close conflict has to become "refresh", not a generic toast.
    const record =
      payload && typeof payload === "object"
        ? (payload as Record<string, unknown>)
        : {};
    throw new ApiError(
      response.status,
      typeof record.code === "string" ? record.code : null,
      messageFromBody(record, response.statusText),
      payload,
    );
  }

  return payload as T;
}

/**
 * An ANONYMOUS read for a public page that has no session (the customer's
 * delivery tracking page, driver backend B4 · P16).
 *
 * Unlike `apiFetch` it sends no cookie (`credentials: "omit"` — a staff
 * session on the same browser must never ride along to a capability-token
 * route), no referrer (the page's own URL carries the token), is never cached,
 * and reports nothing to the network store: a failure here must not trigger
 * the offline probe, which reads `/seller/auth/me`. GET only.
 */
export async function publicApiFetch<T>(
  path: string,
  options: { signal?: AbortSignal } = {},
): Promise<T> {
  const response = await fetch(`${getApiBaseUrl()}${path}`, {
    method: "GET",
    credentials: "omit",
    cache: "no-store",
    referrerPolicy: "no-referrer",
    headers: { Accept: "application/json" },
    signal: options.signal,
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    const record =
      payload && typeof payload === "object"
        ? (payload as Record<string, unknown>)
        : {};
    throw new ApiError(
      response.status,
      typeof record.code === "string" ? record.code : null,
      messageFromBody(record, response.statusText),
      payload,
    );
  }
  return payload as T;
}

/**
 * The one non-JSON read this app makes: the invoice CSV export (D40).
 *
 * A bare `<a href>` to the API origin would not carry the session cookie the
 * same way `apiFetch` does (D1's cross-apex cookie needs `credentials:
 * "include"` on every request), so the file is pulled as a `Blob` through
 * this same fetch surface and handed to the caller to turn into an object URL
 * — never a second, ad hoc `fetch` at the call site.
 *
 * On failure the body is still JSON (the controller's typed refusals, e.g.
 * `RESTAURANT_EXPORT_TOO_LARGE`), so it is parsed the same way `apiFetch`
 * parses one.
 */
export async function apiFetchBlob(path: string): Promise<Blob> {
  const generation = getPrivateSessionState().generation;
  assertPrivateSession(generation);
  const response = await fetch(`${getApiBaseUrl()}${path}`, {
    credentials: "include",
  }).catch(failure => { assertPrivateSession(generation); throw failure; });
  assertPrivateSession(generation);

  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    assertPrivateSession(generation);
    const record =
      payload && typeof payload === "object"
        ? (payload as Record<string, unknown>)
        : {};
    throw new ApiError(
      response.status,
      typeof record.code === "string" ? record.code : null,
      messageFromBody(record, response.statusText),
      payload,
    );
  }

  const blob = await response.blob().catch(failure => { assertPrivateSession(generation); throw failure; });
  assertPrivateSession(generation);
  return blob;
}

/**
 * "Can this device reach the API at all?" — ANY HTTP answer (even a 401) is a
 * yes; only a connection that never happens is a no. Used by the offline
 * screen's probe and nowhere else; it carries no private data and ignores the
 * response.
 */
function probeApiReachable(): Promise<boolean> {
  let base: string;
  try {
    base = getApiBaseUrl();
  } catch {
    // No API base configured: nothing to probe, so no verdict against the link.
    return Promise.resolve(true);
  }
  // XHR, not `fetch`: the probe must not share (or be confused with) the app's
  // own fetch traffic — a failed read is what TRIGGERED the probe, and the
  // specs that script `fetch` call by call would have their sequence eaten by
  // it. `status 0` is the browser's "no response at all".
  return new Promise<boolean>((resolve) => {
    const request = new XMLHttpRequest();
    request.open("GET", `${base}/seller/auth/me`);
    request.withCredentials = true;
    request.timeout = 5000;
    request.onload = () => resolve(request.status !== 0);
    request.onerror = () => resolve(false);
    request.ontimeout = () => resolve(false);
    request.onabort = () => resolve(false);
    request.send();
  });
}
networkStore.setProbe(probeApiReachable);

/** Uncached session discovery is the only private read allowed during a cookie change. */
export async function inspectSession(): Promise<SellerSession | undefined> {
  const controller = new AbortController();
  const deadline = setTimeout(() => controller.abort(), 5000);
  try {
    const response = await fetch(`${getApiBaseUrl()}/seller/auth/me?includeFirebaseToken=true`, {
      credentials: "include", cache: "no-store", signal: controller.signal,
    });
    if (response.status === 401 || response.status === 403) return undefined;
    if (!response.ok) throw new Error("Session inspection failed");
    const session = await response.json() as SellerSession;
    if (!session.seller?.storeSlug || !session.seller.id) throw new Error("Invalid session inspection");
    return session;
  } finally { clearTimeout(deadline); }
}
